/*
 * accept and types take MIME wildcards the way the HTML accept attribute does:
 * 'image' followed by a slash and a star stands for every subtype of image, and
 * a bare star for anything at all. Extensions carry no slash, so a wildcard entry
 * can never match one by accident — which is what lets accept go on holding both
 * kinds of entry side by side.
 */
export type TypeAllowList = {
    exact: Set<string>;
    // Prefixes to match on, each already ending in the slash: 'image/*' → 'image/'.
    groups: string[];
};

// ----------------------------------------------------------------------------

/*
 * Null for a list that allows nothing in particular — no entries, or none left
 * after the empty ones are dropped — which every caller reads as "this rule was
 * not configured" rather than as "nothing passes".
 */
function toAllowList(list: string[] | undefined): TypeAllowList | null {
    if (list == null || list.length === 0) {
        return null;
    }

    const exact = new Set<string>();
    const groups: string[] = [];

    for (const item of normalizeList(list)) {
        if (item === '*' || item === '*/*') {
            // The empty prefix matches every non-empty value.
            groups.push('');
            continue;
        }

        if (item.endsWith('/*')) {
            groups.push(item.slice(0, -1));
            continue;
        }

        exact.add(item);
    }

    if (exact.size === 0 && groups.length === 0) {
        return null;
    }

    return { exact, groups };
}

// ----------------------------------------------------------------------------

function allows(list: TypeAllowList, value: string): boolean {
    if (value === '') {
        return false;
    }

    if (list.exact.has(value)) {
        return true;
    }

    return list.groups.some((group): boolean => value.startsWith(group));
}

// ----------------------------------------------------------------------------

// Lower case, no leading dot, no blanks — the form both sides are compared in.
function normalizeList(list: string[]): string[] {
    return list
        .map((item): string => item.trim().toLowerCase())
        .filter((item): boolean => item.length > 0)
        .map((item): string => (item.startsWith('.') ? item.slice(1) : item));
}

// ----------------------------------------------------------------------------

export { allows, normalizeList, toAllowList };
